import React, { useId, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { Input, InputProps } from '@epoch-ui/components/Input';

import {
  FormField,
  FormControl,
  FormDescription,
  FormLabel,
  FormItem,
  FormMessage
} from '../../form';

export interface MaxLengthInputFieldProps extends InputProps {
  label?: string | React.ReactNode | React.ReactNode[];
  description?: string | React.ReactNode | React.ReactNode[];
  inputClassName?: string;
  maxLength?: number;
}

export const MaxLengthInputField = React.forwardRef<HTMLInputElement, MaxLengthInputFieldProps>(
  ({ name, label, description, className, inputClassName, maxLength, ...props }, ref) => {
    const formCtx = useFormContext();
    const id = useId();
    const [charCount, setCharCount] = useState(formCtx.getValues(name || id)?.length || 0);

    return (
      <FormField
        control={formCtx.control}
        name={name || id}
        render={({ field, fieldState }): React.ReactElement => {
          const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
            const { value } = event.target;

            if (!maxLength || value.length <= maxLength) {
              field.onChange(event);
              setCharCount(value.length);
            }
          };

          return (
            <FormItem className={cn('tw-mt-4', className)}>
              {label && <FormLabel className="tw-mb-0">{label}</FormLabel>}
              {description && <FormDescription className="tw-m-0">{description}</FormDescription>}
              <FormControl>
                <div className="tw-relative !tw-mt-0">
                  <Input
                    {...props}
                    {...field}
                    value={field.value || ''}
                    className={cn('tw-mt-2 tw-pr-16', inputClassName)}
                    error={!!fieldState.error}
                    maxLength={maxLength}
                    ref={ref}
                    onChange={handleInputChange}
                  />
                  {!!maxLength && (
                    <div className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-transform tw-text-sm tw-font-normal tw-leading-5 tw-text-medium-grey">
                      {charCount}/{maxLength}
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  }
);

MaxLengthInputField.displayName = 'MaxLengthInputField';
